import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    // Header
    appTitle: 'Stock Analysis Dashboard',
    welcome: 'Welcome',
    apiStatus: 'API',
    refresh: 'Refresh',
    logout: 'Logout',
    language: 'Language',
    
    // Navigation
    previousStock: 'Previous stock',
    nextStock: 'Next stock',
    
    // Auth & Login
    loading: 'Loading...',
    login: 'Login',
    username: 'Username',
    password: 'Password',
    loginButton: 'Sign In',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    loginFailed: 'Login failed',
    invalidCredentials: 'Invalid username or password',
    tryAgain: 'Try Again',
    
    // API Status
    healthy: 'healthy',
    unhealthy: 'unhealthy',
    checking: 'checking',
    
    // Alerts
    apiConnectionIssue: 'API Connection Issue',
    apiConnectionMessage: 'Unable to connect to the backend API. Please ensure the server is running and you have set up your API key.',
    
    // Footer
    footerText: 'Stock Analysis Dashboard - Built with React & Express',
    footerAttribution: 'Data powered by Yahoo Finance',
    
    // Common Buttons & Actions
    search: 'Search',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    remove: 'Remove',
    close: 'Close',
    open: 'Open',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    confirm: 'Confirm',
    reset: 'Reset',
    clear: 'Clear',
    submit: 'Submit',
    apply: 'Apply',
    export: 'Export',
    import: 'Import',
    
    // Stock Data
    symbol: 'Symbol',
    price: 'Price',
    change: 'Change',
    changePercent: 'Change %',
    volume: 'Volume',
    marketCap: 'Market Cap',
    peRatio: 'P/E Ratio',
    dividendYield: 'Dividend Yield',
    dayHigh: 'Day High',
    dayLow: 'Day Low',
    yearHigh: '52W High',
    yearLow: '52W Low',
    avgVolume: 'Avg Volume',
    beta: 'Beta',
    eps: 'EPS',
    sharesOutstanding: 'Shares Outstanding',
    previousClose: 'Previous Close',
    
    // Analysis & Recommendations
    analysis: 'Analysis',
    recommendation: 'Recommendation',
    buy: 'Buy',
    sell: 'Sell',
    hold: 'Hold',
    strongBuy: 'STRONG BUY',
    strongSell: 'STRONG SELL',
    weakBuy: 'WEAK BUY',
    weakSell: 'WEAK SELL',
    
    // Stock Search
    searchPlaceholder: 'Search stocks...',
    searchStocks: 'Search Stocks',
    noResults: 'No results found',
    searchResults: 'Search Results',
    selectStock: 'Select Stock',
    
    // Portfolio & Watchlist
    portfolio: 'Portfolio',
    watchlist: 'Watchlist',
    addToWatchlist: 'Add to Watchlist',
    removeFromWatchlist: 'Remove from Watchlist',
    inWatchlist: 'In Watchlist',
    notInWatchlist: 'Not in Watchlist',
    emptyWatchlist: 'Your watchlist is empty',
    addFirstStock: 'Add your first stock to get started',
    manageWatchlist: 'Manage Watchlist',
    importWatchlist: 'Import Watchlist',
    exportWatchlist: 'Export Watchlist',
    watchlistCount: 'stocks in watchlist',
    dragToReorder: 'Drag to reorder',
    
    // Charts & Time Ranges
    chart: 'Chart',
    historical: 'Historical Chart',
    intraday: 'Intraday Chart',
    candlestick: 'Candlestick',
    line: 'Line',
    area: 'Area',
    ohlc: 'OHLC',
    oneDay: '1D',
    oneWeek: '1W',
    oneMonth: '1M',
    threeMonths: '3M',
    sixMonths: '6M',
    oneYear: '1Y',
    twoYears: '2Y',
    fiveYears: '5Y',
    allTime: 'All',
    ytd: 'YTD',
    max: 'Max',
    
    // Chart Controls
    chartType: 'Chart Type',
    timeframe: 'Timeframe',
    dateRange: 'Date Range',
    zoom: 'Zoom',
    showAll: 'Show All',
    autoRefresh: 'Auto Refresh',
    manualRefresh: 'Manual Refresh',
    refreshData: 'Refresh Data',
    lastUpdated: 'Last Updated',
    dataFrom: 'Data from',
    
    // Technical Analysis
    fundamentalAnalysis: 'Fundamental Analysis',
    technicalAnalysis: 'Technical Analysis',
    aiAnalysis: 'AI Analysis',
    indicators: 'Indicators',
    movingAverages: 'Moving Averages',
    oscillators: 'Oscillators',
    trend: 'Trend',
    momentum: 'Momentum',
    volatility: 'Volatility',
    support: 'Support',
    resistance: 'Resistance',
    
    // Technical Indicators
    sma: 'SMA',
    ema: 'EMA',
    rsi: 'RSI',
    macd: 'MACD',
    bollingerBands: 'Bollinger Bands',
    stochastic: 'Stochastic',
    williamsR: 'Williams %R',
    cci: 'CCI',
    atr: 'ATR',
    adx: 'ADX',
    
    // Signals & Trends
    bullish: 'Bullish',
    bearish: 'Bearish',
    neutral: 'Neutral',
    uptrend: 'Uptrend',
    downtrend: 'Downtrend',
    sideways: 'Sideways',
    overbought: 'Overbought',
    oversold: 'Oversold',
    buySignal: 'Buy Signal',
    sellSignal: 'Sell Signal',
    
    // Stock Recommendations
    stockRecommendations: 'Stock Recommendations',
    topPicks: 'Top Picks',
    recommendedStocks: 'Recommended Stocks',
    recalculate: 'Recalculate Rankings',
    recalculateRankings: 'Recalculate Rankings',
    rankingCriteria: 'Ranking Criteria',
    performanceScore: 'Performance Score',
    riskLevel: 'Risk Level',
    investmentRating: 'Investment Rating',
    
    // Risk Assessment
    lowRisk: 'Low Risk',
    mediumRisk: 'Medium Risk',
    highRisk: 'High Risk',
    riskFactors: 'Risk Factors',
    riskAssessment: 'Risk Assessment',
    
    // Performance Metrics
    performance: 'Performance',
    returns: 'Returns',
    dailyReturn: 'Daily Return',
    weeklyReturn: 'Weekly Return',
    monthlyReturn: 'Monthly Return',
    quarterlyReturn: 'Quarterly Return',
    yearlyReturn: 'Yearly Return',
    
    // Fundamental Metrics
    fundamentals: 'Fundamentals',
    financialHealth: 'Financial Health',
    profitability: 'Profitability',
    growth: 'Growth',
    valuation: 'Valuation',
    efficiency: 'Efficiency',
    leverage: 'Leverage',
    liquidity: 'Liquidity',
    
    // AI Analysis
    aiInsights: 'AI Insights',
    sentiment: 'Sentiment',
    prediction: 'Prediction',
    confidence: 'Confidence',
    keyFactors: 'Key Factors',
    marketSentiment: 'Market Sentiment',
    priceTarget: 'Price Target',
    
    // Time-based
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This Week',
    lastWeek: 'Last Week',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    thisQuarter: 'This Quarter',
    lastQuarter: 'Last Quarter',
    thisYear: 'This Year',
    lastYear: 'Last Year',
    
    // Status & States
    active: 'Active',
    inactive: 'Inactive',
    enabled: 'Enabled',
    disabled: 'Disabled',
    online: 'Online',
    offline: 'Offline',
    connected: 'Connected',
    disconnected: 'Disconnected',
    
    // Error Messages
    error: 'Error',
    errorOccurred: 'An error occurred',
    noData: 'No data available',
    dataNotFound: 'Data not found',
    connectionError: 'Connection error',
    timeout: 'Request timeout',
    retry: 'Retry',
    
    // Success Messages
    success: 'Success',
    completed: 'Completed',
    saved: 'Saved',
    updated: 'Updated',
    addedSuccessfully: 'Added successfully',
    removedSuccessfully: 'Removed successfully',
    
    // Fundamental Analysis
    financialHealthScore: 'Financial Health Score',
    howScoreCalculated: 'How is this score calculated?',
    scoreCalculation: 'Score Calculation (Starting at 50)',
    pbRatio: 'P/B Ratio',
    revenue: 'Revenue',
    revenueGrowth: 'Revenue Growth',
    profitMargin: 'Profit Margin',
    debtToEquity: 'Debt/Equity',
    roe: 'ROE',
    roa: 'ROA',
    analystOpinion: 'Analyst Opinion',
    targetPrice: 'Target Price',
    priceRange52w: 'Price Range (52w)',
    volumeAvg: 'Volume (Avg)',
    keyInsights: 'Key Insights',
    attractiveValuation: 'Attractive valuation with P/E below 15',
    lowDebtLevels: 'Low debt levels indicate financial stability',
    strongROE: 'Strong ROE indicates efficient management',
    highDebtWarning: 'High debt levels may pose financial risk',
    highPEWarning: 'High P/E ratio suggests overvaluation',
    lowProfitability: 'Low profitability needs attention',
    strongRevenueGrowth: 'Strong revenue growth trend',
    decliningRevenue: 'Declining revenue requires attention',
    highValuationWarning: 'High valuation may indicate overpricing',
    
    // Score status
    excellent: 'Excellent',
    good: 'Good',
    fair: 'Fair',
    poor: 'Poor',
    unknown: 'Unknown',
    
    // Technical Analysis
    technicalIndicators: 'Technical Indicators',
    momentumIndicators: 'Momentum Indicators',
    volatilityIndicators: 'Volatility Indicators',
    trendAnalysis: 'Trend Analysis',
    tradingSignals: 'Trading Signals',
    priceAction: 'Price Action',
    volumeAnalysis: 'Volume Analysis',
    supportResistance: 'Support & Resistance',
    
    // Technical Indicator Details
    sma20: 'SMA (20)',
    sma50: 'SMA (50)',
    sma200: 'SMA (200)',
    ema12: 'EMA (12)',
    ema26: 'EMA (26)',
    rsiIndicator: 'RSI',
    macdIndicator: 'MACD',
    stochasticIndicator: 'Stochastic',
    
    // Signal interpretations
    signalStrength: 'Signal Strength',
    strong: 'Strong',
    moderate: 'Moderate',
    weak: 'Weak',
    bullishCrossover: 'Bullish Crossover',
    bearishCrossover: 'Bearish Crossover',
    aboveResistance: 'Above Resistance',
    belowSupport: 'Below Support',
    breakoutPattern: 'Breakout Pattern',
    consolidation: 'Consolidation',
    
    // Analysis conclusions
    overallTrend: 'Overall Trend',
    shortTermOutlook: 'Short-term Outlook',
    longTermOutlook: 'Long-term Outlook',
    tradingRecommendation: 'Trading Recommendation',
    
    // Technical Analysis Signal Messages
    priceAboveMA: 'Price above 20 & 50 MA - Bullish trend',
    priceBelowMA: 'Price below 20 & 50 MA - Bearish trend',
    goldenCrossSignal: '50 MA crossed above 200 MA - Strong bullish signal',
    deathCrossSignal: '50 MA crossed below 200 MA - Strong bearish signal',
    rsiOverbought: 'RSI > 70 - Overbought condition',
    rsiOversold: 'RSI < 30 - Oversold condition',
    macdBullish: 'MACD above signal line - Bullish momentum',
    macdBearish: 'MACD below signal line - Bearish momentum',
    bbUpperBreakout: 'Price above upper band - Potential reversal',
    bbLowerBounce: 'Price below lower band - Potential bounce',
    noSignals: 'No strong trading signals detected at this time',
    
    // Languages
    english: 'English',
    chinese: '中文'
  },
  zh: {
    // Header
    appTitle: '股票分析仪表板',
    welcome: '欢迎',
    apiStatus: 'API',
    refresh: '刷新',
    logout: '退出',
    language: '语言',
    
    // Navigation
    previousStock: '上一只股票',
    nextStock: '下一只股票',
    
    // Auth & Login
    loading: '加载中...',
    login: '登录',
    username: '用户名',
    password: '密码',
    loginButton: '登录',
    showPassword: '显示密码',
    hidePassword: '隐藏密码',
    loginFailed: '登录失败',
    invalidCredentials: '用户名或密码无效',
    tryAgain: '重试',
    
    // API Status
    healthy: '健康',
    unhealthy: '不健康',
    checking: '检查中',
    
    // Alerts
    apiConnectionIssue: 'API连接问题',
    apiConnectionMessage: '无法连接到后端API。请确保服务器正在运行且您已设置API密钥。',
    
    // Footer
    footerText: '股票分析仪表板 - 基于React和Express构建',
    footerAttribution: '数据由Yahoo Finance提供',
    
    // Common Buttons & Actions
    search: '搜索',
    save: '保存',
    cancel: '取消',
    delete: '删除',
    edit: '编辑',
    add: '添加',
    remove: '移除',
    close: '关闭',
    open: '打开',
    back: '返回',
    next: '下一个',
    previous: '上一个',
    confirm: '确认',
    reset: '重置',
    clear: '清空',
    submit: '提交',
    apply: '应用',
    export: '导出',
    import: '导入',
    
    // Stock Data
    symbol: '股票代码',
    price: '价格',
    change: '变动',
    changePercent: '涨跌幅',
    volume: '成交量',
    marketCap: '市值',
    peRatio: '市盈率',
    dividendYield: '股息率',
    dayHigh: '今日最高',
    dayLow: '今日最低',
    yearHigh: '52周最高',
    yearLow: '52周最低',
    avgVolume: '平均成交量',
    beta: '贝塔系数',
    eps: '每股收益',
    sharesOutstanding: '流通股数',
    previousClose: '昨收价',
    
    // Analysis & Recommendations
    analysis: '分析',
    recommendation: '推荐',
    buy: '买入',
    sell: '卖出',
    hold: '持有',
    strongBuy: '强烈买入',
    strongSell: '强烈卖出',
    weakBuy: '弱势买入',
    weakSell: '弱势卖出',
    
    // Stock Search
    searchPlaceholder: '搜索股票...',
    searchStocks: '股票搜索',
    noResults: '未找到结果',
    searchResults: '搜索结果',
    selectStock: '选择股票',
    
    // Portfolio & Watchlist
    portfolio: '投资组合',
    watchlist: '关注列表',
    addToWatchlist: '添加到关注列表',
    removeFromWatchlist: '从关注列表中移除',
    inWatchlist: '已在关注列表',
    notInWatchlist: '未在关注列表',
    emptyWatchlist: '您的关注列表为空',
    addFirstStock: '添加您的第一只股票开始',
    manageWatchlist: '管理关注列表',
    importWatchlist: '导入关注列表',
    exportWatchlist: '导出关注列表',
    watchlistCount: '只股票在关注列表中',
    dragToReorder: '拖拽重新排序',
    
    // Charts & Time Ranges
    chart: '图表',
    historical: '历史图表',
    intraday: '分时图表',
    candlestick: '蜡烛图',
    line: '线图',
    area: '面积图',
    ohlc: '开高低收',
    oneDay: '1日',
    oneWeek: '1周',
    oneMonth: '1月',
    threeMonths: '3月',
    sixMonths: '6月',
    oneYear: '1年',
    twoYears: '2年',
    fiveYears: '5年',
    allTime: '全部',
    ytd: '年初至今',
    max: '最大',
    
    // Chart Controls
    chartType: '图表类型',
    timeframe: '时间周期',
    dateRange: '日期范围',
    zoom: '缩放',
    showAll: '显示全部',
    autoRefresh: '自动刷新',
    manualRefresh: '手动刷新',
    refreshData: '刷新数据',
    lastUpdated: '最后更新',
    dataFrom: '数据来源',
    
    // Technical Analysis
    fundamentalAnalysis: '基本面分析',
    technicalAnalysis: '技术分析',
    aiAnalysis: 'AI分析',
    indicators: '指标',
    movingAverages: '移动平均线',
    oscillators: '震荡指标',
    trend: '趋势',
    momentum: '动量',
    volatility: '波动率',
    support: '支撑位',
    resistance: '阻力位',
    
    // Technical Indicators
    sma: '简单移动平均',
    ema: '指数移动平均',
    rsi: '相对强弱指数',
    macd: 'MACD',
    bollingerBands: '布林线',
    stochastic: '随机指标',
    williamsR: '威廉指标',
    cci: '商品通道指数',
    atr: '平均真实范围',
    adx: '平均方向指数',
    
    // Signals & Trends
    bullish: '看涨',
    bearish: '看跌',
    neutral: '中性',
    uptrend: '上升趋势',
    downtrend: '下降趋势',
    sideways: '横盘整理',
    overbought: '超买',
    oversold: '超卖',
    buySignal: '买入信号',
    sellSignal: '卖出信号',
    
    // Stock Recommendations
    stockRecommendations: '股票推荐',
    topPicks: '精选股票',
    recommendedStocks: '推荐股票',
    recalculate: '重新计算',
    recalculateRankings: '重新计算排名',
    rankingCriteria: '排名标准',
    performanceScore: '表现得分',
    riskLevel: '风险等级',
    investmentRating: '投资评级',
    
    // Risk Assessment
    lowRisk: '低风险',
    mediumRisk: '中等风险',
    highRisk: '高风险',
    riskFactors: '风险因素',
    riskAssessment: '风险评估',
    
    // Performance Metrics
    performance: '表现',
    returns: '收益',
    dailyReturn: '日收益',
    weeklyReturn: '周收益',
    monthlyReturn: '月收益',
    quarterlyReturn: '季度收益',
    yearlyReturn: '年收益',
    
    // Fundamental Metrics
    fundamentals: '基本面',
    financialHealth: '财务健康',
    profitability: '盈利能力',
    growth: '成长性',
    valuation: '估值',
    efficiency: '效率',
    leverage: '杠杆',
    liquidity: '流动性',
    
    // AI Analysis
    aiInsights: 'AI洞察',
    sentiment: '情绪',
    prediction: '预测',
    confidence: '置信度',
    keyFactors: '关键因素',
    marketSentiment: '市场情绪',
    priceTarget: '目标价格',
    
    // Time-based
    today: '今天',
    yesterday: '昨天',
    thisWeek: '本周',
    lastWeek: '上周',
    thisMonth: '本月',
    lastMonth: '上月',
    thisQuarter: '本季度',
    lastQuarter: '上季度',
    thisYear: '今年',
    lastYear: '去年',
    
    // Status & States
    active: '活跃',
    inactive: '非活跃',
    enabled: '已启用',
    disabled: '已禁用',
    online: '在线',
    offline: '离线',
    connected: '已连接',
    disconnected: '已断开',
    
    // Error Messages
    error: '错误',
    errorOccurred: '发生错误',
    noData: '无数据',
    dataNotFound: '未找到数据',
    connectionError: '连接错误',
    timeout: '请求超时',
    retry: '重试',
    
    // Success Messages
    success: '成功',
    completed: '已完成',
    saved: '已保存',
    updated: '已更新',
    addedSuccessfully: '添加成功',
    removedSuccessfully: '移除成功',
    
    // Fundamental Analysis
    financialHealthScore: '财务健康评分',
    howScoreCalculated: '评分如何计算？',
    scoreCalculation: '评分计算（起始50分）',
    pbRatio: '市净率',
    revenue: '营收',
    revenueGrowth: '营收增长',
    profitMargin: '利润率',
    debtToEquity: '负债权益比',
    roe: '净资产收益率',
    roa: '资产收益率',
    analystOpinion: '分析师观点',
    targetPrice: '目标价格',
    priceRange52w: '52周价格区间',
    volumeAvg: '平均成交量',
    keyInsights: '关键洞察',
    attractiveValuation: '市盈率低于15，估值具吸引力',
    lowDebtLevels: '负债水平低，显示财务稳定',
    strongROE: '净资产收益率强劲，显示管理高效',
    highDebtWarning: '高负债水平可能带来财务风险',
    highPEWarning: '高市盈率可能显示估值过高',
    lowProfitability: '盈利能力低需要关注',
    strongRevenueGrowth: '营收增长趋势强劲',
    decliningRevenue: '营收下降需要关注',
    highValuationWarning: '高估值可能显示定价过高',
    
    // Score status
    excellent: '优秀',
    good: '良好',
    fair: '一般',
    poor: '较差',
    unknown: '未知',
    
    // Technical Analysis
    technicalIndicators: '技术指标',
    momentumIndicators: '动量指标',
    volatilityIndicators: '波动性指标',
    trendAnalysis: '趋势分析',
    tradingSignals: '交易信号',
    priceAction: '价格行为',
    volumeAnalysis: '成交量分析',
    supportResistance: '支撑与阻力',
    
    // Technical Indicator Details
    sma20: '简单移动平均(20)',
    sma50: '简单移动平均(50)',
    sma200: '简单移动平均(200)',
    ema12: '指数移动平均(12)',
    ema26: '指数移动平均(26)',
    rsiIndicator: '相对强弱指数',
    macdIndicator: 'MACD指标',
    stochasticIndicator: '随机指标',
    
    // Signal interpretations
    signalStrength: '信号强度',
    strong: '强',
    moderate: '中等',
    weak: '弱',
    bullishCrossover: '看涨交叉',
    bearishCrossover: '看跌交叉',
    aboveResistance: '突破阻力位',
    belowSupport: '跌破支撑位',
    breakoutPattern: '突破形态',
    consolidation: '盘整',
    
    // Analysis conclusions
    overallTrend: '整体趋势',
    shortTermOutlook: '短期前景',
    longTermOutlook: '长期前景',
    tradingRecommendation: '交易建议',
    
    // Technical Analysis Signal Messages
    priceAboveMA: '价格高于20和50日均线 - 看涨趋势',
    priceBelowMA: '价格低于20和50日均线 - 看跌趋势',
    goldenCrossSignal: '50日均线上穿200日均线 - 强烈看涨信号',
    deathCrossSignal: '50日均线下穿200日均线 - 强烈看跌信号',
    rsiOverbought: 'RSI > 70 - 超买状态',
    rsiOversold: 'RSI < 30 - 超卖状态',
    macdBullish: 'MACD高于信号线 - 看涨动量',
    macdBearish: 'MACD低于信号线 - 看跌动量',
    bbUpperBreakout: '价格突破上轨 - 可能反转',
    bbLowerBounce: '价格跌破下轨 - 可能反弹',
    noSignals: '当前未检测到强烈交易信号',
    
    // Languages
    english: 'English',
    chinese: '中文'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get saved language from localStorage or default to English
    return localStorage.getItem('preferredLanguage') || 'en';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('preferredLanguage', language);
  }, [language]);

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
  };

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const value = {
    language,
    changeLanguage,
    t,
    translations: translations[language] || translations.en
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};